import React, { useState, useEffect, useRef } from 'react';
import { Bell, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function NotificationToast() {
  const [notifications, setNotifications] = useState([]);
  const [show, setShow] = useState(false);
  const navigate = useNavigate();
  const retryCountRef = useRef(0);
  const maxRetries = 3; // Maximum 3 kez dene
  
  useEffect(() => {
    // WebSocket ile bildirim dinle
    const token = localStorage.getItem('token');
    if (!token) return;
    
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.hostname === 'localhost' ? 'localhost:8001' : window.location.host;
    const wsUrl = `${protocol}//${host}/api/ws/notifications`;
    
    let ws;
    let reconnectTimeout;
    
    const connectWebSocket = () => {
      // Maximum retry sayısını aştıysa bağlanma
      if (retryCountRef.current >= maxRetries) {
        console.log('🔔 Notification WebSocket: Max retries reached, stopping reconnection');
        return;
      }
      
      try {
        ws = new WebSocket(`${wsUrl}?token=${token}`);
        
        ws.onopen = () => {
          console.log(' Notification WebSocket connected');
          retryCountRef.current = 0; // Başarılı bağlantıda retry sayacını sıfırla
        };
        
        ws.onmessage = (event) => {
          try {
            const notification = JSON.parse(event.data);
            
            // Ping mesajlarını atla
            if (notification.type === 'ping') return;
            
            console.log('🔔 New notification:', notification);
            
            // Yeni bildirimi ekle
            setNotifications(prev => [...prev, {
              id: Date.now(),
              message: notification.message,
              type: notification.type || 'info',
              timestamp: new Date()
            }]);
            
            setShow(true);
            
            // 5 saniye sonra otomatik kapat
            setTimeout(() => {
              setShow(false);
              setTimeout(() => {
                setNotifications(prev => prev.slice(1));
              }, 300);
            }, 5000);
            
          } catch (error) {
            console.error('Notification parse error:', error);
          }
        };
        
        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
        };
        
        ws.onclose = () => {
          retryCountRef.current += 1;
          if (retryCountRef.current < maxRetries) {
            console.log(`🔔 Notification WebSocket closed, retry ${retryCountRef.current}/${maxRetries}...`);
            reconnectTimeout = setTimeout(connectWebSocket, 10000); // 10 saniye bekle
          } else {
            console.log('🔔 Notification WebSocket: Stopped reconnection after max retries');
          }
        };
        
      } catch (error) {
        console.error('WebSocket connection error:', error);
      }
    };
    
    connectWebSocket();
    
    return () => {
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (ws) ws.close();
    };
  }, []);
  
  const handleClick = () => {
    navigate('/notifications');
    setShow(false);
  };
  
  const handleClose = () => {
    setShow(false);
    setTimeout(() => {
      setNotifications(prev => prev.slice(1));
    }, 300);
  };
  
  if (notifications.length === 0 || !show) return null;
  
  const latestNotification = notifications[0];
  
  return (
    <div 
      className={`fixed top-20 left-4 z-50 transition-all duration-300 ${
        show ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'
      }`}
    >
      <div 
        className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg shadow-2xl p-4 max-w-sm cursor-pointer hover:shadow-3xl transition-all"
        onClick={handleClick}
      >
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="bg-white bg-opacity-20 rounded-full p-2 flex-shrink-0">
            <Bell className="w-5 h-5" />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-bold text-sm">Yeni Bildirim</h4>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {/* Notification Count */}
            {notifications.length > 1 && (
              <div className="text-xs text-blue-200 mb-2">
                {notifications.length} yeni bildirim
              </div>
            )}
            
            {/* Message Preview */}
            <p className="text-sm text-blue-100 line-clamp-2 break-words">
              {latestNotification.message}
            </p>
            
            {/* Timestamp */}
            <p className="text-xs text-blue-300 mt-2">
              {latestNotification.timestamp.toLocaleTimeString('tr-TR', { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotificationToast;
